import { useCallback, useEffect, useRef } from "react";
import { useUserScopedSettings } from "./useUserScopedSettings";

/** A group reduced to what has to be stored to find it again. */
interface StoredGroup {
  id: string;
  name?: string;
}

interface GroupFilterSettings extends Record<string, unknown> {
  /** Whether the user asked for their group filter to be remembered. Off unless they say so. */
  remember: boolean;
  /** Empty while not remembering, so opting out leaves nothing behind. */
  groups: StoredGroup[];
}

const NOTHING_REMEMBERED: GroupFilterSettings = { remember: false, groups: [] };

export interface RememberedGroups<TGroup> {
  remember: boolean;
  /** Turning this on stores what is selected now; turning it off forgets it. */
  setRemember: (remember: boolean) => void;
  /** Call whenever the user changes the selection. */
  recordSelection: (groups: TGroup[]) => void;
}

interface Options<TGroup> {
  /** Every group the user could pick, once loaded. */
  available: TGroup[] | undefined;
  /** What is selected right now, so turning the switch on can store it. */
  selected: TGroup[] | undefined;
  /** Called at most once, with the groups to put back. */
  onRestore: (groups: TGroup[]) => void;
}

/**
 * Remembers the group filter for the signed-in user, if they ask for it.
 *
 * Off by default, and deliberately so: the group filter decides which vehicles every page in the
 * add-in shows, and a filter that reappears by itself is indistinguishable from a fleet that has
 * lost vehicles. It is only ever restored because the user ticked a box saying to.
 *
 * Not `useRememberedSelection`, which remembers one option out of a list and falls back to the
 * first: here nothing selected means every vehicle, so falling back to the first group would
 * hide the rest of the fleet.
 *
 * @param scope - Names the filter. Must be stable.
 * @param options - See {@link Options}.
 */
export function useRememberedGroups<TGroup extends { id: string; name?: string }>(
  scope: string,
  { available, selected, onRestore }: Options<TGroup>,
): RememberedGroups<TGroup> {
  const { settings, setSettings, isFetched } = useUserScopedSettings<GroupFilterSettings>(
    scope,
    NOTHING_REMEMBERED,
  );

  const hasRestored = useRef(false);

  useEffect(() => {
    if (hasRestored.current || !isFetched) {
      return;
    }
    // The stored record and the group list arrive from separate requests, so wait for the list
    // before deciding a remembered group no longer exists.
    if (!available || available.length === 0) {
      return;
    }

    hasRestored.current = true;

    if (!settings.remember || settings.groups.length === 0) {
      return;
    }

    const stillThere = settings.groups
      .map(stored => available.find(group => group.id === stored.id))
      .filter((group): group is TGroup => !!group);

    // Nothing left to put back — every remembered group has been deleted since. Restoring an
    // empty selection would mean the same as the unfiltered state the page is already in, so
    // there is nothing to do and no reason to write to the shared state.
    if (stillThere.length > 0) {
      onRestore(stillThere);
    }
  }, [available, isFetched, onRestore, settings.groups, settings.remember]);

  const recordSelection = useCallback(
    (groups: TGroup[]) => {
      setSettings(current =>
        current.remember ? { ...current, groups: groups.map(toStored) } : current,
      );
    },
    [setSettings],
  );

  const setRemember = useCallback(
    (remember: boolean) => {
      // Storing the current selection as the switch goes on saves the user from re-picking what
      // is already on screen; clearing it as the switch goes off leaves nothing behind.
      setSettings(current => ({
        ...current,
        remember,
        groups: remember ? (selected ?? []).map(toStored) : [],
      }));
      // The user has taken charge of this; nothing should be put back over their choice later.
      hasRestored.current = true;
    },
    [selected, setSettings],
  );

  return { remember: settings.remember, setRemember, recordSelection };
}

function toStored(group: { id: string; name?: string }): StoredGroup {
  return { id: group.id, name: group.name };
}
